import { BetType, BetTypeKey } from '../../models/bet-type.model';
import { Odd, OddKey } from '../../models/odd.model';
import { SelectionResolverId } from '../config/sportsbook-config.types';
import { MatchDetailSectionViewModel } from '../sportsbook.models';
import {
  SportsbookLocalizationContext,
  SportsbookLabelMatch,
  resolveAwayTeamLabel,
  resolveHomeTeamLabel,
  resolveLocalizedOddLabel
} from './sportsbook-localization.utils';
import {
  assertNever,
  formatCorrectScoreLabel,
  formatLineValue,
  splitSpecialOddsValue,
  trimToNull
} from './sportsbook-shared.utils';

const SPECIAL_ODDS_VALUE_LABEL_BET_TYPES = new Set<BetTypeKey>([
  BetTypeKey.UNDER_OVER,
  BetTypeKey.UNDER_OVER_H1,
  BetTypeKey.REST_OF_MATCH_H1,
  BetTypeKey.REST_OF_MATCH,
  BetTypeKey.HANDICAP,
  BetTypeKey.NEXT_GOAL,
  BetTypeKey.NEXT_GOAL_H1,
  BetTypeKey.UNDER_OVER_OT,
  BetTypeKey.PERIOD_WINNER,
  BetTypeKey.TENNIS_UNDER_OVER,
  BetTypeKey.TENNIS_SET_WINNER,
  BetTypeKey.WAY_3_UNDER_OVER,
  BetTypeKey.TENNIS_SET_CORRECT_SCORE,
  BetTypeKey.UNDER_OVER_H2,
  BetTypeKey.HANDICAP_H1,
  BetTypeKey.HANDICAP_H2,
  BetTypeKey.UNDER_OVER_CORNER,
  BetTypeKey.UNDER_OVER_HOMETEAM,
  BetTypeKey.UNDER_OVER_AWAYTEAM,
  BetTypeKey.REST_OF_MATCH_OT,
  BetTypeKey.TENNIS_GAME_X_WINNER,
  BetTypeKey.TENNIS_SET_UNDER_OVER,
  BetTypeKey.UNDER_OVER_PERIOD,
  BetTypeKey.XTH_GOAL_PERIOD,
  BetTypeKey.REST_OF_MATCH_PERIOD,
  BetTypeKey.HIGHEST_SCORE_PERIOD
]);

export function buildContextualDetailLabel(marketLabel: string, selectionLabel: string): string {
  const normalizedMarketLabel = trimToNull(marketLabel);
  const normalizedSelectionLabel = trimToNull(selectionLabel);

  if (!normalizedMarketLabel) {
    return normalizedSelectionLabel ?? '';
  }

  if (!normalizedSelectionLabel || normalizedSelectionLabel === normalizedMarketLabel) {
    return normalizedMarketLabel;
  }

  return normalizedSelectionLabel.startsWith(`${normalizedMarketLabel} - `)
    ? normalizedSelectionLabel
    : `${normalizedMarketLabel} - ${normalizedSelectionLabel}`;
}

export function buildDetailMarketKey(betType: BetType): string {
  return `detail:${betType.Id}`;
}

export function buildSelectionMarketKey(betType: BetType | null | undefined, fallbackKey: string): string {
  return betType ? buildDetailMarketKey(betType) : fallbackKey;
}

export function compareBetTypes(left: BetType, right: BetType): number {
  const leftValue = getSpecialValue(left);
  const rightValue = getSpecialValue(right);

  if (leftValue !== null && rightValue !== null && leftValue !== rightValue) {
    return leftValue - rightValue;
  }

  const leftText = getSpecialValueText(left);
  const rightText = getSpecialValueText(right);
  if (leftText && rightText && leftText !== rightText) {
    return leftText.localeCompare(rightText, undefined, { numeric: true });
  }

  if (left.Key !== right.Key) {
    return left.Key - right.Key;
  }

  return left.Id - right.Id;
}

export function resolveMatchDetailLayout(
  preferredLayout: MatchDetailSectionViewModel['layout'],
  optionCount: number
): MatchDetailSectionViewModel['layout'] {
  if (preferredLayout === 'list') {
    return 'list';
  }

  if (preferredLayout === 'grid3' && optionCount <= 2) {
    return 'grid2';
  }

  return preferredLayout;
}

export function getSpecialValue(betType: BetType): number | null {
  const specialValue = getSpecialValueText(betType);
  if (!specialValue) {
    return null;
  }

  const normalizedSpecialValue = specialValue.replace(',', '.');
  if (!/^-?\d+(?:\.\d+)?$/.test(normalizedSpecialValue)) {
    return null;
  }

  const numericValue = Number.parseFloat(normalizedSpecialValue);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getSpecialValueText(betType: BetType): string | null {
  return trimToNull(betType.SpecialOddsValue);
}

export function resolveSelectionLabel(
  selectionResolver: SelectionResolverId,
  match: SportsbookLabelMatch,
  lineValue: number | null,
  context: SportsbookLocalizationContext
): string {
  switch (selectionResolver) {
    case 'home':
      return resolveHomeTeamLabel(match, context.currentLanguage);
    case 'draw':
      return context.t('sportsbook.common.draw');
    case 'away':
      return resolveAwayTeamLabel(match, context.currentLanguage);
    case 'over':
      return lineValue === null
        ? context.t('sportsbook.common.over')
        : `${context.t('sportsbook.common.over')} ${formatLineValue(lineValue)}`;
    case 'under':
      return lineValue === null
        ? context.t('sportsbook.common.under')
        : `${context.t('sportsbook.common.under')} ${formatLineValue(lineValue)}`;
    case 'yes':
      return context.t('sportsbook.common.yes');
    case 'no':
      return context.t('sportsbook.common.no');
    default:
      return assertNever(selectionResolver);
  }
}

export function resolveHeaderLabel(
  header: string,
  selectionResolver: SelectionResolverId,
  lineValue: number | null,
  t: SportsbookLocalizationContext['t']
): string {
  const localizedHeader =
    selectionResolver === 'over'
      ? t('sportsbook.common.overShort')
      : selectionResolver === 'under'
        ? t('sportsbook.common.underShort')
        : header;

  if (selectionResolver === 'over' || selectionResolver === 'under') {
    return lineValue === null ? localizedHeader : `${localizedHeader} ${formatLineValue(lineValue)}`;
  }

  return localizedHeader;
}

export function resolveDetailOddLabel(
  match: SportsbookLabelMatch,
  betType: BetType,
  odd: Odd,
  context: SportsbookLocalizationContext
): string | null {
  switch (odd.Key) {
    case OddKey.ROM_1:
    case OddKey.ROM_H1_1:
    case OddKey.RESULT_1:
    case OddKey.ROM_OT_1:
    case OddKey.RESULT_OT_1:
    case OddKey.WAY2_1:
    case OddKey.TENNIS_MW_1:
    case OddKey.TENNIS_SW_1:
    case OddKey.TENNIS_GW_1:
    case OddKey.RESULT_H1_1:
    case OddKey.RESULT_H2_1:
    case OddKey.PERIOD_WINNER_1:
    case OddKey.ROM_PERIOD_1:
    case OddKey.DNB_1:
    case OddKey.HC_1:
    case OddKey.HC_H1_1:
    case OddKey.HC_H2_1:
    case OddKey.NG_1:
    case OddKey.NG_OT_1:
    case OddKey.NG_H1_1:
    case OddKey.LG_1:
    case OddKey.KICKOFF_1:
    case OddKey.PENALTY_1:
    case OddKey.RACE_X_POINTS_1:
    case OddKey.SCORE_X_POINT_1:
    case OddKey.ADV_NXT_RND_1:
      return appendSpecialValueContext(resolveHomeTeamLabel(match, context.currentLanguage), betType, context);
    case OddKey.ROM_X:
    case OddKey.ROM_H1_X:
    case OddKey.RESULT_X:
    case OddKey.ROM_OT_X:
    case OddKey.RESULT_OT_X:
    case OddKey.TENNIS_GW_X:
    case OddKey.RESULT_H1_X:
    case OddKey.RESULT_H2_X:
    case OddKey.PERIOD_WINNER_X:
    case OddKey.ROM_PERIOD_X:
    case OddKey.HC_X:
    case OddKey.HC_H1_X:
    case OddKey.HC_H2_X:
      return appendSpecialValueContext(context.t('sportsbook.common.draw'), betType, context);
    case OddKey.ROM_2:
    case OddKey.ROM_H1_2:
    case OddKey.RESULT_2:
    case OddKey.ROM_OT_2:
    case OddKey.RESULT_OT_2:
    case OddKey.WAY2_2:
    case OddKey.TENNIS_MW_2:
    case OddKey.TENNIS_SW_2:
    case OddKey.TENNIS_GW_2:
    case OddKey.RESULT_H1_2:
    case OddKey.RESULT_H2_2:
    case OddKey.PERIOD_WINNER_2:
    case OddKey.ROM_PERIOD_2:
    case OddKey.DNB_2:
    case OddKey.HC_2:
    case OddKey.HC_H1_2:
    case OddKey.HC_H2_2:
    case OddKey.NG_2:
    case OddKey.NG_OT_2:
    case OddKey.NG_H1_2:
    case OddKey.LG_2:
    case OddKey.KICKOFF_2:
    case OddKey.PENALTY_2:
    case OddKey.RACE_X_POINTS_2:
    case OddKey.SCORE_X_POINT_2:
    case OddKey.ADV_NXT_RND_2:
      return appendSpecialValueContext(resolveAwayTeamLabel(match, context.currentLanguage), betType, context);
    case OddKey.NG_X:
    case OddKey.NG_OT_X:
    case OddKey.NG_H1_X:
    case OddKey.LG_X:
    case OddKey.XTHG_PERIOD_X:
      return appendSpecialValueContext(context.t('sportsbook.common.noGoal'), betType, context);
    case OddKey.XTHG_PERIOD_1:
      return appendSpecialValueContext(resolveHomeTeamLabel(match, context.currentLanguage), betType, context);
    case OddKey.XTHG_PERIOD_2:
      return appendSpecialValueContext(resolveAwayTeamLabel(match, context.currentLanguage), betType, context);
    case OddKey.UO_O:
    case OddKey.UO_OT_O:
    case OddKey.UO_H1_O:
    case OddKey.UO_H2_O:
    case OddKey.TENNIS_UO_O:
    case OddKey.TENNIS_SET_UO_O:
    case OddKey.UO_PERIOD_O:
    case OddKey.UO_CORNER_O:
    case OddKey.UO_CORNER_H1_O:
      return requiresSpecialValueContextOnLineLabel(betType)
        ? appendSpecialValueContext(buildLineLabel(context.t('sportsbook.common.over'), betType), betType, context)
        : buildLineLabel(context.t('sportsbook.common.over'), betType);
    case OddKey.UO_U:
    case OddKey.UO_OT_U:
    case OddKey.UO_H1_U:
    case OddKey.UO_H2_U:
    case OddKey.TENNIS_UO_U:
    case OddKey.TENNIS_SET_UO_U:
    case OddKey.UO_PERIOD_U:
    case OddKey.UO_CORNER_U:
    case OddKey.UO_CORNER_H1_U:
      return requiresSpecialValueContextOnLineLabel(betType)
        ? appendSpecialValueContext(buildLineLabel(context.t('sportsbook.common.under'), betType), betType, context)
        : buildLineLabel(context.t('sportsbook.common.under'), betType);
    case OddKey.UO_HOMETEAM_O:
    case OddKey.UO_HOMETEAM_H1_O:
    case OddKey.UO_CORNER_HT_O:
      return buildParticipantLineLabel(resolveHomeTeamLabel(match, context.currentLanguage), context.t('sportsbook.common.over'), betType);
    case OddKey.UO_HOMETEAM_U:
    case OddKey.UO_HOMETEAM_H1_U:
    case OddKey.UO_CORNER_HT_U:
      return buildParticipantLineLabel(resolveHomeTeamLabel(match, context.currentLanguage), context.t('sportsbook.common.under'), betType);
    case OddKey.UO_AWAYTEAM_O:
    case OddKey.UO_AWAYTEAM_H1_O:
    case OddKey.UO_CORNER_AT_O:
      return buildParticipantLineLabel(resolveAwayTeamLabel(match, context.currentLanguage), context.t('sportsbook.common.over'), betType);
    case OddKey.UO_AWAYTEAM_U:
    case OddKey.UO_AWAYTEAM_H1_U:
    case OddKey.UO_CORNER_AT_U:
      return buildParticipantLineLabel(resolveAwayTeamLabel(match, context.currentLanguage), context.t('sportsbook.common.under'), betType);
    case OddKey.DC_1X:
      return `${resolveHomeTeamLabel(match, context.currentLanguage)} ${context.t('sportsbook.common.or')} ${context.t('sportsbook.common.draw')}`;
    case OddKey.DC_12:
      return `${resolveHomeTeamLabel(match, context.currentLanguage)} ${context.t('sportsbook.common.or')} ${resolveAwayTeamLabel(match, context.currentLanguage)}`;
    case OddKey.DC_X2:
      return `${context.t('sportsbook.common.draw')} ${context.t('sportsbook.common.or')} ${resolveAwayTeamLabel(match, context.currentLanguage)}`;
    case OddKey.GNG_GOAL:
    case OddKey.GNG_H1_GOAL:
    case OddKey.GNG_H2_GOAL:
    case OddKey.PSO_YES:
      return context.t('sportsbook.common.yes');
    case OddKey.GNG_NOGOAL:
    case OddKey.GNG_H1_NOGOAL:
    case OddKey.GNG_H2_NOGOAL:
    case OddKey.PSO_NO:
      return context.t('sportsbook.common.no');
    case OddKey.GOAL_ODD:
      return context.t('sportsbook.common.odd');
    case OddKey.GOAL_EVEN:
      return context.t('sportsbook.common.even');
    default:
      return resolveGenericOddLabel(match, odd, betType, context);
  }
}

function buildLineLabel(prefix: string, betType: BetType): string {
  const lineValue = resolveLineValueLabel(betType);
  return lineValue === null ? prefix : `${prefix} ${lineValue}`;
}

function buildParticipantLineLabel(participant: string, prefix: string, betType: BetType): string {
  const participantLabel = participant.trim();
  if (!participantLabel) {
    return buildLineLabel(prefix, betType);
  }

  return `${participantLabel} ${buildLineLabel(prefix, betType)}`;
}

function appendSpecialValueContext(label: string, betType: BetType, context: SportsbookLocalizationContext): string {
  const specialValueContext = resolveSpecialValueContext(betType, context);
  if (!specialValueContext) {
    return label;
  }

  return label.includes(specialValueContext) ? label : `${label} (${specialValueContext})`;
}

function resolveSpecialValueContext(betType: BetType, context: SportsbookLocalizationContext): string | null {
  const specialValue = getSpecialValueText(betType);
  if (!specialValue || !SPECIAL_ODDS_VALUE_LABEL_BET_TYPES.has(betType.Key)) {
    return null;
  }

  const { primary, secondary } = splitSpecialOddsValue(specialValue);

  switch (betType.Key) {
    case BetTypeKey.TENNIS_SET_WINNER:
    case BetTypeKey.TENNIS_SET_UNDER_OVER:
    case BetTypeKey.TENNIS_SET_CORRECT_SCORE:
      return `${context.t('sportsbook.common.set')} ${primary ?? specialValue}`;
    case BetTypeKey.TENNIS_GAME_X_WINNER:
      if (primary && secondary) {
        return `${context.t('sportsbook.common.set')} ${primary}, ${context.t('sportsbook.common.game')} ${secondary}`;
      }

      return `${context.t('sportsbook.common.game')} ${secondary ?? specialValue}`;
    case BetTypeKey.PERIOD_WINNER:
    case BetTypeKey.UNDER_OVER_PERIOD:
    case BetTypeKey.XTH_GOAL_PERIOD:
    case BetTypeKey.REST_OF_MATCH_PERIOD:
    case BetTypeKey.HIGHEST_SCORE_PERIOD:
      return `${context.t('sportsbook.common.period')} ${primary ?? specialValue}`;
    default:
      return specialValue;
  }
}

function resolveGenericOddLabel(
  match: SportsbookLabelMatch,
  odd: Odd,
  betType: BetType,
  context: SportsbookLocalizationContext
): string | null {
  const label = resolveLocalizedOddLabel(odd, context.currentLanguage) ?? resolveGeneratedOddLabel(match, odd, context);
  if (!label) {
    return null;
  }

  return appendSpecialValueContext(label, betType, context);
}

function resolveGeneratedOddLabel(match: SportsbookLabelMatch, odd: Odd, context: SportsbookLocalizationContext): string | null {
  const oddKeyLabel = OddKey[odd.Key];
  if (typeof oddKeyLabel !== 'string') {
    return resolveOddFieldTypeLabel(odd.OddFieldType, context)
      ?? context.t('sportsbook.common.unknownBet');
  }

  if (oddKeyLabel.startsWith('CS_H1_')) {
    return formatCorrectScoreLabel(oddKeyLabel.replace('CS_H1_', ''));
  }

  if (oddKeyLabel.startsWith('CS_')) {
    return formatCorrectScoreLabel(oddKeyLabel.replace('CS_', ''));
  }

  const mappedLabel = resolveEnumOddLabel(match, oddKeyLabel, context);
  if (mappedLabel) {
    return mappedLabel;
  }

  return resolveOddFieldTypeLabel(odd.OddFieldType, context)
    ?? context.t('sportsbook.common.unknownBet');
}

function resolveLineValueLabel(betType: BetType): string | null {
  const specialValue = getSpecialValueText(betType);
  if (!specialValue) {
    return null;
  }

  const { secondary } = splitSpecialOddsValue(specialValue);
  switch (betType.Key) {
    case BetTypeKey.TENNIS_SET_UNDER_OVER:
    case BetTypeKey.UNDER_OVER_PERIOD:
      return secondary ?? specialValue;
    default:
      return specialValue;
  }
}

function requiresSpecialValueContextOnLineLabel(betType: BetType): boolean {
  return betType.Key === BetTypeKey.TENNIS_SET_UNDER_OVER || betType.Key === BetTypeKey.UNDER_OVER_PERIOD;
}

function resolveEnumOddLabel(match: SportsbookLabelMatch, oddKeyLabel: string, context: SportsbookLocalizationContext): string | null {
  const directOddLabels: Partial<Record<string, string>> = {
    GOAL_ODD: context.t('sportsbook.common.odd'),
    GOAL_EVEN: context.t('sportsbook.common.even'),
    FG15_NONE: context.t('sportsbook.common.noGoal'),
    CS_H1_OTHER: context.t('sportsbook.common.other')
  };

  const directLabel = directOddLabels[oddKeyLabel];
  if (directLabel) {
    return directLabel;
  }

  if (/^HT_FT_[12X]{2}$/.test(oddKeyLabel)) {
    const outcome = oddKeyLabel.replace('HT_FT_', '');
    return `${outcome[0]}/${outcome[1]}`;
  }

  if (oddKeyLabel.startsWith('TENNIS_RESULTS3_')) {
    return formatCorrectScoreLabel(oddKeyLabel.replace('TENNIS_RESULTS3_', ''));
  }

  if (oddKeyLabel.startsWith('TENNIS_RESULTS5_')) {
    return formatCorrectScoreLabel(oddKeyLabel.replace('TENNIS_RESULTS5_', ''));
  }

  if (oddKeyLabel.startsWith('TENNIS_SET_CS_')) {
    return formatCorrectScoreLabel(oddKeyLabel.replace('TENNIS_SET_CS_', ''));
  }

  if (oddKeyLabel.startsWith('TENNIS_SETS3_') || oddKeyLabel.startsWith('TENNIS_SETS5_')) {
    const setCount = oddKeyLabel.replace(/^TENNIS_SETS[35]_/, '');
    return `${setCount} ${context.t('sportsbook.common.sets')}`;
  }

  if (oddKeyLabel.startsWith('TOTAL_GOALS_EXACT_H1_')) {
    return formatExactGoalCountLabel(oddKeyLabel.replace('TOTAL_GOALS_EXACT_H1_', ''), context);
  }

  if (oddKeyLabel.startsWith('TOTAL_GOALS_EXACT_H2_')) {
    return formatExactGoalCountLabel(oddKeyLabel.replace('TOTAL_GOALS_EXACT_H2_', ''), context);
  }

  if (oddKeyLabel.startsWith('TOTAL_GOALS_EXACT_')) {
    return formatExactGoalCountLabel(oddKeyLabel.replace('TOTAL_GOALS_EXACT_', ''), context);
  }

  if (oddKeyLabel.startsWith('TOTAL_GOALS_')) {
    return oddKeyLabel.replace('TOTAL_GOALS_', '').replace('_PLUS', '+').replace('_', '-');
  }

  if (oddKeyLabel.startsWith('CORNER_TOTAL_')) {
    return oddKeyLabel.replace('CORNER_TOTAL_', '').replace('_PLUS', '+').replace('_', '-');
  }

  if (oddKeyLabel.startsWith('FG15_')) {
    return oddKeyLabel.replace('FG15_', '').replace('_', '-');
  }

  if (oddKeyLabel.startsWith('GHT_') || oddKeyLabel.startsWith('GAT_')) {
    return oddKeyLabel
      .replace(/^G[HA]T_/, '')
      .replace('ZERO', '0')
      .replace('ONE', '1')
      .replace('TWO', '2')
      .replace('THREE_PLUS', '3+');
  }

  if (oddKeyLabel.startsWith('HSH_') || oddKeyLabel.startsWith('CORNER_W3_')) {
    return oddKeyLabel.replace(/^(HSH_|CORNER_W3_)/, '');
  }

  if (oddKeyLabel.startsWith('WAY_3_INT10_')) {
    return oddKeyLabel.replace('WAY_3_INT10_', '');
  }

  if (oddKeyLabel.startsWith('UO_GNG_')) {
    return resolveLineAndGoalNoGoalComboLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('W3_UO_') || oddKeyLabel.startsWith('W3_UO_H1_') || oddKeyLabel.startsWith('W3_UO_H2_')) {
    return resolveOutcomeAndLineComboLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('WAY_3_GNG_') || oddKeyLabel.startsWith('W3_GNG_H1_') || oddKeyLabel.startsWith('W3_GNG_H2_')) {
    return resolveOutcomeAndGoalNoGoalComboLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('DC_UO_')) {
    return resolveDoubleChanceAndLineComboLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('DC_GNG_')) {
    return resolveDoubleChanceAndGoalNoGoalComboLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('DC_H1_') || oddKeyLabel.startsWith('DC_H2_')) {
    return oddKeyLabel.replace(/^DC_H[12]_/, '');
  }

  if (oddKeyLabel.startsWith('MS_')) {
    return resolveMultiScoreLabel(oddKeyLabel, context);
  }

  if (oddKeyLabel.startsWith('W3_OR_W3H1_') || oddKeyLabel.startsWith('W3_XUP_')) {
    return oddKeyLabel.replace('W3_OR_W3H1_', '').replace('W3_XUP_', '');
  }

  if (oddKeyLabel.startsWith('OUTRIGHT_W_')) {
    return oddKeyLabel.replace('OUTRIGHT_W_', '');
  }

  if (oddKeyLabel.startsWith('XTHG_W3_')) {
    return resolveXthGoalWay3OddLabel(oddKeyLabel);
  }

  if (oddKeyLabel.startsWith('HSP_')) {
    return oddKeyLabel === 'HSP_X' ? context.t('sportsbook.common.draw') : oddKeyLabel.replace('HSP_', '');
  }

  if (oddKeyLabel.startsWith('WTN_') || oddKeyLabel.startsWith('WEH_') || oddKeyLabel.startsWith('GNG_BH_')) {
    return oddKeyLabel.endsWith('_YES')
      ? context.t('sportsbook.common.yes')
      : context.t('sportsbook.common.no');
  }

  if (oddKeyLabel === 'TENNIS_GAME_X_WINNER_1') {
    return resolveHomeTeamLabel(match, context.currentLanguage);
  }

  if (oddKeyLabel === 'TENNIS_GAME_X_WINNER_2') {
    return resolveAwayTeamLabel(match, context.currentLanguage);
  }

  return null;
}

function resolveLineAndGoalNoGoalComboLabel(
  oddKeyLabel: string,
  context: SportsbookLocalizationContext
): string {
  const normalized = oddKeyLabel.replace('UO_GNG_', '');
  const firstSeparatorIndex = normalized.indexOf('_');
  if (firstSeparatorIndex === -1) {
    return oddKeyLabel;
  }

  const line = normalized.slice(0, firstSeparatorIndex) === 'O'
    ? context.t('sportsbook.common.over')
    : context.t('sportsbook.common.under');
  const goalNoGoal = normalized.slice(firstSeparatorIndex + 1) === 'GOAL'
    ? context.t('sportsbook.common.yes')
    : context.t('sportsbook.common.no');

  return `${goalNoGoal} + ${line}`;
}

function resolveOddFieldTypeLabel(
  oddFieldType: string,
  context: SportsbookLocalizationContext
): string | null {
  const fieldType = trimToNull(oddFieldType);
  if (!fieldType) {
    return null;
  }

  switch (fieldType.toLowerCase()) {
    case 'o':
    case 'over':
      return context.t('sportsbook.common.over');
    case 'u':
    case 'under':
      return context.t('sportsbook.common.under');
    case 'goal':
    case 'yes':
      return context.t('sportsbook.common.yes');
    case 'nogoal':
    case 'no':
      return context.t('sportsbook.common.no');
    default:
      return fieldType;
  }
}

function resolveOutcomeAndLineComboLabel(oddKeyLabel: string, context: SportsbookLocalizationContext): string {
  const normalized = oddKeyLabel
    .replace('W3_UO_H1_', '')
    .replace('W3_UO_H2_', '')
    .replace('W3_UO_', '');

  if (normalized.length < 2) {
    return oddKeyLabel;
  }

  const outcome = normalized.slice(0, -1);
  const line = normalized.slice(-1) === 'O'
    ? context.t('sportsbook.common.over')
    : context.t('sportsbook.common.under');

  return `${outcome} + ${line}`;
}

function resolveOutcomeAndGoalNoGoalComboLabel(oddKeyLabel: string, context: SportsbookLocalizationContext): string {
  const normalized = oddKeyLabel
    .replace('WAY_3_GNG_', '')
    .replace('W3_GNG_H1_', '')
    .replace('W3_GNG_H2_', '');

  const separatorIndex = normalized.lastIndexOf('_');
  if (separatorIndex === -1) {
    return oddKeyLabel;
  }

  const outcome = normalized.slice(0, separatorIndex);
  const goalNoGoal = normalized.slice(separatorIndex + 1) === 'GOAL'
    ? context.t('sportsbook.common.yes')
    : context.t('sportsbook.common.no');

  return `${outcome} + ${goalNoGoal}`;
}

function resolveDoubleChanceAndLineComboLabel(oddKeyLabel: string, context: SportsbookLocalizationContext): string {
  const normalized = oddKeyLabel.replace('DC_UO_', '');
  const separatorIndex = normalized.lastIndexOf('_');
  if (separatorIndex === -1) {
    return oddKeyLabel;
  }

  const choice = normalized.slice(0, separatorIndex);
  const line = normalized.slice(separatorIndex + 1) === 'O'
    ? context.t('sportsbook.common.over')
    : context.t('sportsbook.common.under');

  return `${choice} + ${line}`;
}

function resolveDoubleChanceAndGoalNoGoalComboLabel(oddKeyLabel: string, context: SportsbookLocalizationContext): string {
  const normalized = oddKeyLabel.replace('DC_GNG_', '');
  const separatorIndex = normalized.lastIndexOf('_');
  if (separatorIndex === -1) {
    return oddKeyLabel;
  }

  const choice = normalized.slice(0, separatorIndex);
  const goalNoGoal = normalized.slice(separatorIndex + 1) === 'GOAL'
    ? context.t('sportsbook.common.yes')
    : context.t('sportsbook.common.no');

  return `${choice} + ${goalNoGoal}`;
}

function resolveMultiScoreLabel(oddKeyLabel: string, context: SportsbookLocalizationContext): string {
  switch (oddKeyLabel) {
    case 'MS_10_20_30':
      return '1:0, 2:0, 3:0';
    case 'MS_01_02_03':
      return '0:1, 0:2, 0:3';
    case 'MS_40_50_60':
      return '4:0, 5:0, 6:0';
    case 'MS_04_05_06':
      return '0:4, 0:5, 0:6';
    case 'MS_21_31_41':
      return '2:1, 3:1, 4:1';
    case 'MS_12_13_14':
      return '1:2, 1:3, 1:4';
    case 'MS_32_42_43_51':
      return '3:2, 4:2, 4:3, 5:1';
    case 'MS_23_24_34_15':
      return '2:3, 2:4, 3:4, 1:5';
    case 'MS_OTHER_HOMETEAM':
      return context.t('sportsbook.multiscore.homeOther');
    case 'MS_OTHER_AWAYTEAM':
      return context.t('sportsbook.multiscore.awayOther');
    case 'MS_DRAW':
      return context.t('sportsbook.multiscore.draw');
    default:
      return oddKeyLabel.replace(/^MS_/, '').replace(/_/g, ', ');
  }
}

function resolveXthGoalWay3OddLabel(oddKeyLabel: string): string {
  switch (oddKeyLabel) {
    case 'XTHG_W3_HTGOAL_1':
      return '1/1';
    case 'XTHG_W3_HTGOAL_X':
      return 'X/1';
    case 'XTHG_W3_HTGOAL_2':
      return '2/1';
    case 'XTHG_W3_ATGOAL_1':
      return '1/2';
    case 'XTHG_W3_ATGOAL_X':
      return 'X/2';
    case 'XTHG_W3_ATGOAL_2':
      return '2/2';
    case 'XTHG_W3_NOGOOAL':
      return 'X/X';
    default:
      return oddKeyLabel;
  }
}

function formatExactGoalCountLabel(rawCount: string, context: SportsbookLocalizationContext): string {
  const count = rawCount.replace('_PLUS', '+');
  if (count === '1') {
    return `1 ${context.t('sportsbook.common.goal')}`;
  }

  return `${count} ${context.t('sportsbook.common.goals')}`;
}
